import React, { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { DateRange } from '@material-ui/pickers';
import { useStyles } from './styles';
import { useApiError, useLoading, useAppData } from '../../../providers';
import { Header, ReportGeneralToolbarWithFilters, PageTitle, ReportTitle } from '../../../components';
import { PositionsReport, reportEnColumns, OpenClosedPositionsEmailReportRequest } from './OpenClosedPositionsSummaryModel';
import { ReportTable } from './components';
import { getPositionsReport, sendEmailOpenClosedPositions } from './OpenClosedPositionsSummaryService';
import { getSelectedColumnsInEn, getTranslatedColumnsInCorrectOrder } from '../../../utils';
import { trackAmplitudeData } from '../../../services/amplitudeService';
import { useCommonStyles } from '../../../styles';
import { ColumnConfig, formatNumber } from '../DataTable';
import { formatInteger } from '../DataTable/formatters';

export const OpenClosedPositionsSummary: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { setIsLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const { addError } = useApiError();
  const { translations, userData } = useAppData();
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const [selectedLogins, setSelectedLogins] = useState<string[]>(['All']);
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [reportData, setReportData] = useState<PositionsReport[]>([]);
  const [translation, setTranslation] = useState<any>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string>();

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    const user = JSON.parse(userData);
    setUserEmail(user.userEmail);
  }, [userData]);

  const columns = useMemo((): ColumnConfig<PositionsReport>[] => {
    const openLabel = translation.text_1219 || '';
    const closedLabel = translation.text_1220 || '';

    const getSubLabel = (label: string): string | undefined => {
      if (!label) return undefined;
      if (label.indexOf(openLabel) !== -1) return openLabel;
      if (label.indexOf(closedLabel) !== -1) return closedLabel;
      return undefined;
    };

    return [
      { key: 'login', label: translation.text_6643 || '', type: 'number' },
      { key: 'partnershipCode', label: translation.text_6642 || '', type: 'string' },
      { key: 'openedVolumeUSD', label: translation.text_6644 || '', subLabel: getSubLabel(translation.text_6644 || ''), type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'openLots', label: translation.text_6645 || '', subLabel: getSubLabel(translation.text_6645 || ''), type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'openTrades', label: translation.text_6646 || '', subLabel: getSubLabel(translation.text_6646 || ''), type: 'number', format: (v) => formatInteger(v), summable: true },
      { key: 'openPnL', label: translation.text_6647 || '', subLabel: getSubLabel(translation.text_6647 || ''), type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'closedVolumeUSD', label: translation.text_6648 || '', subLabel: getSubLabel(translation.text_6648 || ''), type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'closedLots', label: translation.text_6649 || '', subLabel: getSubLabel(translation.text_6649 || ''), type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'closedTrades', label: translation.text_6650 || '', subLabel: getSubLabel(translation.text_6650 || ''), type: 'number', format: (v) => formatInteger(v), summable: true },
      { key: 'closedPnL', label: translation.text_6651 || '', subLabel: getSubLabel(translation.text_6651 || ''), type: 'number', format: (v) => formatNumber(v), summable: true },
    ];
  }, [translation]);

  // Derived string[] needed only for email utilities
  const columnNames = useMemo(() => columns.map((col) => col.label), [columns]);

  useEffect(() => {
    if (columnNames.length === 10) {
      setSelectedColumns(columnNames.slice(0, 5));
    }
  }, [columnNames]);

  const getReportData = useCallback(
    async (partnerships: string[], logins: string[]) => {
      setIsLoading(true);
      try {
        trackAmplitudeData('partnerPortal_action_report', {
          type: 'Requested',
          report_name: 'Open Closed Positions Summary',
        });

        const response = await getPositionsReport({
          partnershipCodes: partnerships.includes('All') ? [] : partnerships,
          fromDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
          toDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
          pageNumber: 0,
          login: logins.includes('All') ? [] : logins,
        });
        setReportData(response.data);
        setShowReport(true);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [selectedDateRange, addError, setIsLoading],
  );

  const selectPartnershipChangeHandler = useCallback(
    async (options: any) => {
      setSelectedPartnerships(options);
      await getReportData(options, selectedLogins);
    },
    [getReportData, selectedLogins],
  );

  const selectLoginChangeHandler = useCallback(
    async (options: any) => {
      setSelectedLogins(options);
      await getReportData(selectedPartnerships, options);
    },
    [getReportData, selectedPartnerships],
  );

  const handleNewReportRequest = useCallback(async () => {
    await getReportData(selectedPartnerships, selectedLogins);
  }, [selectedPartnerships, getReportData, selectedLogins]);

  const buildOpenClosedPositionsEmailReportRequest = (): OpenClosedPositionsEmailReportRequest => ({
    reportPartnershipClientsTradingRequest: {
      fromDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
      toDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
      partnershipCodes: selectedPartnerships.includes('All') ? [] : selectedPartnerships,
      pageNumber: 1,
      login: selectedLogins.includes('All') ? [] : selectedLogins,
    },
    reportEmailRequest: {
      email: null,
      includeTotal: false,
      tableColumns: getSelectedColumnsInEn(reportEnColumns, selectedColumns, columnNames),
      translatedTableColumns: getTranslatedColumnsInCorrectOrder(selectedColumns, columnNames),
      startDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
    },
  });

  const sendEmail = async () => {
    const openClosedPositionsEmailReportRequest = buildOpenClosedPositionsEmailReportRequest();

    trackAmplitudeData('partnerPortal_action_report', {
      type: 'SendByEmail',
      report_name: 'Open Closed Positions Summary',
    });

    try {
      await sendEmailOpenClosedPositions(openClosedPositionsEmailReportRequest);
      enqueueSnackbar(`${translation.text_1640} ${userEmail}.`, { variant: 'success' });
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            translation={translation}
            title={translation.text_6481}
            subTitle={translation.openClosedPosiitionsTooltip}
            exportFileName={translation.text_6481}
            columns={columns}
            allData={reportData}
            selectedColumns={selectedColumns}
            content={<ReportTable rows={reportData} selectedColumns={selectedColumns} columns={columns} translation={translation} onSelectColumns={setSelectedColumns} />}
            printColumnNumber={columnNames.length}
            enableButtons={showReport}
            sendEmail={sendEmail}
            reportNameInEn="Open Closed Positions Summary"
          />
          <Grid container direction="column">
            <ReportGeneralToolbarWithFilters
              translation={translation}
              dateRange={selectedDateRange}
              handleDateRangeChange={(dateRange) => {
                setSelectedDateRange(dateRange);
                setShowReport(false);
              }}
              handlePartnershipsChange={selectPartnershipChangeHandler}
              handleButtonClick={handleNewReportRequest}
              handleLoginsChange={selectLoginChangeHandler}
              applyFilters={async (partnerships, logins) => {
                setSelectedPartnerships(partnerships);
                setSelectedLogins(logins);
                await getReportData(partnerships, logins);
              }}
            />
            <Grid item xs={12} className={classes.margin}>
              <ReportTable rows={reportData} columns={columns} translation={translation} selectedColumns={selectedColumns} onSelectColumns={setSelectedColumns} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
